import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  ChevronDown,
  Hash,
  LayoutDashboard,
  LineChart,
  ListChecks,
  PenSquare,
  Plus,
  Search,
  Settings,
  Sparkles,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import { type Business } from '../../lib/api';
import { getBusinessList } from '../../lib/businessListCache';
import { useSelectedBusinessId } from '../../hooks/useSelectedBusiness';

interface NavItem {
  section: string;
  label: string;
  icon: LucideIcon;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [{ section: 'dashboard', label: 'Обзор', icon: LayoutDashboard }],
  },
  {
    label: 'Репутация',
    items: [{ section: 'reviews', label: 'Отзывы', icon: Star }],
  },
  {
    label: 'Карточка и SEO',
    items: [
      { section: 'services', label: 'Услуги', icon: ListChecks },
      { section: 'keywords', label: 'Ключи', icon: Hash },
      { section: 'content', label: 'Контент', icon: PenSquare },
    ],
  },
  {
    label: 'Аналитика',
    items: [
      { section: 'reports', label: 'Отчёты', icon: BarChart3 },
      { section: 'analytics', label: 'Аналитика', icon: LineChart },
      { section: 'optimization', label: 'Оптимизация', icon: Sparkles },
    ],
  },
];

interface ClientSwitcherProps {
  businesses: Business[];
  selectedId: string | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

function ClientSwitcher({
  businesses,
  selectedId,
  loading,
  error,
  onRetry,
}: ClientSwitcherProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const current = useMemo(
    () => businesses.find((b) => b.id === selectedId) ?? null,
    [businesses, selectedId],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return businesses;
    return businesses.filter((b) => b.name.toLowerCase().includes(q));
  }, [businesses, query]);

  useEffect(() => {
    if (!open) return;

    const onClick = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="w-full flex items-center gap-2 rounded-lg bg-sidebar-border px-3 py-2.5 text-left transition-colors hover:bg-white/10"
      >
        <span className="min-w-0 flex-1">
          <span className="block text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted">
            Клиент
          </span>
          <span className="block truncate text-sm font-medium text-white">
            {loading
              ? 'Загрузка…'
              : current
                ? current.name
                : 'Выберите клиента'}
          </span>
        </span>
        <ChevronDown
          className={clsx(
            'h-4 w-4 shrink-0 text-sidebar-muted transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 rounded-lg border border-sidebar-border bg-sidebar shadow-card">
          <div className="border-b border-sidebar-border p-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-sidebar-muted" />
              <input
                type="text"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Поиск клиента"
                className="w-full rounded-md border border-sidebar-border bg-white/5 py-1.5 pl-8 pr-2 text-sm text-white placeholder:text-sidebar-muted focus:border-primary-500 focus:outline-none"
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto py-1" role="listbox">
            {loading ? (
              <p className="px-3 py-2 text-sm text-sidebar-muted">Загрузка…</p>
            ) : error ? (
              <div className="px-3 py-2 text-sm text-sidebar-muted">
                <p>Не удалось загрузить клиентов</p>
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-1 text-primary-300 transition-colors hover:text-primary-200"
                >
                  Повторить
                </button>
              </div>
            ) : filtered.length === 0 ? (
              <p className="px-3 py-2 text-sm text-sidebar-muted">Ничего не найдено</p>
            ) : (
              filtered.map((business) => (
                <button
                  key={business.id}
                  type="button"
                  role="option"
                  aria-selected={business.id === selectedId}
                  onClick={() => go(`/client/${business.id}/dashboard`)}
                  className={clsx(
                    'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors',
                    business.id === selectedId
                      ? 'bg-primary-600/20 text-primary-200'
                      : 'text-sidebar-muted hover:bg-white/5 hover:text-white',
                  )}
                >
                  <span className="truncate">{business.name}</span>
                </button>
              ))
            )}
          </div>

          <div className="border-t border-sidebar-border p-1">
            <button
              type="button"
              onClick={() => go('/businesses')}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-sidebar-muted transition-colors hover:bg-white/5 hover:text-white"
            >
              <Users className="h-4 w-4" />
              Все клиенты
            </button>
            <button
              type="button"
              onClick={() => go('/businesses?new=1')}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-primary-300 transition-colors hover:bg-white/5 hover:text-primary-200"
            >
              <Plus className="h-4 w-4" />
              Добавить клиента
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Sidebar() {
  const selectedId = useSelectedBusinessId();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        setError(false);
        const data = await getBusinessList();
        if (!active) return;
        setBusinesses(data);
      } catch (err) {
        if (!active) return;
        console.error('Failed to load sidebar data', err);
        setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [reloadToken]);

  const reloadBusinesses = () => setReloadToken((t) => t + 1);

  const navItemClass = (isActive: boolean) =>
    clsx(
      'flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors',
      isActive
        ? 'bg-primary-600/20 text-primary-200'
        : 'text-sidebar-muted hover:bg-white/5 hover:text-white',
    );

  return (
    <aside className="fixed left-0 top-0 z-40 flex h-screen w-64 flex-col border-r border-sidebar-border bg-sidebar">
      {/* Logo */}
      <div className="flex h-16 items-center gap-3 px-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
          <BarChart3 className="h-5 w-5 text-white" />
        </div>
        <span className="font-semibold text-white">GeoMarketolog</span>
      </div>

      {/* Client switcher */}
      <div className="px-3 pb-2">
        <ClientSwitcher
          businesses={businesses}
          selectedId={selectedId}
          loading={loading}
          error={error}
          onRetry={reloadBusinesses}
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-3">
        {NAV_GROUPS.map((group) => (
          <div key={group.label ?? group.items[0].section} className="space-y-1">
            {group.label && (
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted/70">
                {group.label}
              </p>
            )}
            {group.items.map((item) => {
              const Icon = item.icon;

              if (!selectedId) {
                return (
                  <div
                    key={item.section}
                    aria-disabled="true"
                    className="flex cursor-not-allowed items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-sidebar-muted/40"
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.section}
                  to={`/client/${selectedId}/${item.section}`}
                  className={({ isActive }) => navItemClass(isActive)}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span className="flex-1">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="mt-auto border-t border-sidebar-border px-3 py-3">
        <NavLink to="/settings" className={({ isActive }) => navItemClass(isActive)}>
          <Settings className="h-4 w-4 shrink-0" />
          <span className="flex-1">Настройки</span>
        </NavLink>
      </div>
    </aside>
  );
}
